// Live log viewer — initial fetch via HTTP, streaming appends via WS.
// Auto-scrolls to bottom unless the user has scrolled up.
import { useCallback, useEffect, useRef, useState } from "react";
import { httpClient } from "../../client/http-client";
import { wsClient } from "../../client/ws-client";

interface LogViewerProps {
	profileId: string;
}

export const LogViewer = ({ profileId }: LogViewerProps) => {
	const [lines, setLines] = useState<string[]>([]);
	const [loading, setLoading] = useState(true);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const stickToBottomRef = useRef(true);

	const scrollToBottom = useCallback(() => {
		const el = containerRef.current;
		if (!el) return;
		el.scrollTop = el.scrollHeight;
	}, []);

	// Initial fetch
	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		httpClient.backend
			.getLogs(profileId, 500)
			.then((fetched) => {
				if (cancelled) return;
				setLines(fetched);
				setLoading(false);
				requestAnimationFrame(scrollToBottom);
			})
			.catch(() => {
				if (!cancelled) {
					setLines([]);
					setLoading(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [profileId, scrollToBottom]);

	// Live tail via WS
	useEffect(() => {
		const unsubscribe = wsClient.onBackendLog((incomingProfileId, event) => {
			if (incomingProfileId !== profileId) return;
			setLines((prev) => {
				const next = [...prev, event.line];
				if (next.length > 1000) next.splice(0, next.length - 1000);
				return next;
			});
		});
		return unsubscribe;
	}, [profileId]);

	// Keep stuck-to-bottom behavior
	useEffect(() => {
		if (stickToBottomRef.current) {
			scrollToBottom();
		}
	}, [lines, scrollToBottom]);

	const handleScroll = useCallback(() => {
		const el = containerRef.current;
		if (!el) return;
		const atBottom =
			el.scrollHeight - el.scrollTop - el.clientHeight < 40;
		stickToBottomRef.current = atBottom;
	}, []);

	const handleClear = useCallback(async () => {
		await httpClient.backend.clearLogs(profileId);
		setLines([]);
	}, [profileId]);

	return (
		<section className="tg-surface flex h-[28rem] min-h-0 flex-col rounded-2xl">
			<header className="flex items-center justify-between border-b border-[color:var(--color-tg-line)] px-4 py-3">
				<div>
					<h3 className="tg-text-primary text-[0.95rem] font-semibold leading-5">
						Live logs
					</h3>
					<p className="tg-text-muted text-[0.8rem] leading-5">
						Last {lines.length} lines from the child backend process.
					</p>
				</div>
				<button
					type="button"
					onClick={() => void handleClear()}
					className="tg-btn-secondary tg-text-secondary inline-flex h-8 items-center rounded-lg px-3 text-[0.8rem] font-semibold"
				>
					Clear
				</button>
			</header>

			<div
				ref={containerRef}
				onScroll={handleScroll}
				className="tg-log-panel tg-scrollbar flex-1 overflow-y-auto px-4 py-3 font-mono text-[0.78rem] leading-[1.5]"
			>
				{loading ? (
					<div className="tg-text-muted">Loading logs...</div>
				) : lines.length === 0 ? (
					<div className="tg-text-muted">
						No logs yet. Start the backend to see output here.
					</div>
				) : (
					lines.map((line, idx) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: log lines are append-only
						<div key={idx} className="tg-text-secondary break-all">
							{line}
						</div>
					))
				)}
			</div>
		</section>
	);
};
